import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Footer from './Footer';
import SearchPalette from './SearchPalette';
import AccessibilityPanel from './AccessibilityPanel';
import EventPopup from './EventPopup';
import SEOHead from './SEOHead';
import { useTheme } from '../context/ThemeContext';
import { getColors } from '../styles/colors';
import * as S from '../styles/components/Layout.styles';
import menuData from '../data/components/menu.json';

// Menu constants
const ANIMATION_DURATION = 0.6;
const MENU_SIZE = 500;
const RADIUS = 180;
const CENTER = MENU_SIZE / 2; // Always exactly half of container size

// Mobile values
const MENU_SIZE_TABLET = 400;
const RADIUS_TABLET = 140;
const CENTER_TABLET = MENU_SIZE_TABLET / 2;

const MENU_SIZE_MOBILE = 350;
const RADIUS_MOBILE = 120;
const CENTER_MOBILE = MENU_SIZE_MOBILE / 2;

// Menu items paths (paths bleiben gleich, Labels werden dynamisch geladen)
const menuItemPaths = [
  { path: '#yoga-outdoor', key: 'yogaOutdoor' },
  { path: '#yoga-online', key: 'yogaOnline' },
  { path: '#testimonials', key: 'testimonials' },
  { path: '#newsletter', key: 'newsletter' },
  { path: '#contact', key: 'contact' }
];
const kamanaSubmenu: any[] = [];

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [isScrolled, setIsScrolled] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [headerScrollY, setHeaderScrollY] = useState(0);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isAccessibilityOpen, setIsAccessibilityOpen] = useState(false);
  const [pendingScrollAnchor, setPendingScrollAnchor] = useState<string | null>(null);
  const [isMenuPopupOpen, setIsMenuPopupOpen] = useState(false);
  const [menuRotation, setMenuRotation] = useState(0);
  const [isMenuRotating, setIsMenuRotating] = useState(false);
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const [popupOpacity, setPopupOpacity] = useState(1);
  const [isManualScrolling, setIsManualScrolling] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  
  // Track last scroll position
  const lastScrollYRef = React.useRef(0);
  const headerScrollYRef = React.useRef(0);
  
  // Trigger animation on mount
  useEffect(() => {
    if (location.pathname === '/' && !hasAnimated) {
      setHasAnimated(true);
    }
  }, [location.pathname, hasAnimated]);
  
  // Language state - load from localStorage or default to 'de'
  // Prüfe ob funktionale Cookies erlaubt sind
  const [language, setLanguage] = useState<'de' | 'en'>(() => {
    // Prüfe Cookie-Präferenzen
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (cookieConsent) {
      const savedPrefs = localStorage.getItem('cookiePreferences');
      if (savedPrefs) {
        try {
          const prefs = JSON.parse(savedPrefs);
          if (!prefs.functional) {
            // Funktionale Cookies nicht erlaubt - nicht aus localStorage laden
            return 'de';
          }
        } catch (e) {
          // Fehler beim Parsen - Standard verwenden
        }
      }
    }
    const stored = localStorage.getItem('siteLanguage');
    return (stored === 'en' || stored === 'de') ? stored : 'de';
  });
  
  const colors = getColors(isDarkMode);
  
  // Get menu items with translated labels based on current language
  type MenuItem = { path: string; label: string };
  const menuItems: MenuItem[] = menuItemPaths.map(item => ({
    path: item.path,
    label: menuData[language as 'de' | 'en'][item.key as keyof typeof menuData.de]
  }));
  
  // Update language in localStorage and HTML lang attribute
  useEffect(() => {
    document.documentElement.setAttribute('lang', language);
    // Dispatch custom event for other components to listen
    window.dispatchEvent(new CustomEvent('languageChanged', { detail: language }));
    
    // Nur in localStorage speichern, wenn funktionale Cookies erlaubt sind
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (cookieConsent) {
      const savedPrefs = localStorage.getItem('cookiePreferences');
      if (savedPrefs) {
        try {
          const prefs = JSON.parse(savedPrefs);
          if (prefs.functional) {
            localStorage.setItem('siteLanguage', language);
          }
          // Wenn nicht erlaubt: nicht speichern, aber Sprache trotzdem ändern
        } catch (e) {
          // Bei Fehler: speichern (Standard-Verhalten)
          localStorage.setItem('siteLanguage', language);
        }
      } else {
        // Keine Präferenzen gespeichert: speichern (Standard-Verhalten)
        localStorage.setItem('siteLanguage', language);
      }
    } else {
      // Keine Cookie-Zustimmung: speichern (Standard-Verhalten bis User entscheidet)
      localStorage.setItem('siteLanguage', language);
    }
  }, [language]);
  
  const handleLanguageToggle = () => {
    setLanguage(prev => prev === 'de' ? 'en' : 'de');
  };
  
  // Escape-Taste zum Schließen des Menüs
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMenuPopupOpen) {
        setIsMenuPopupOpen(false);
        setIsMenuRotating(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMenuPopupOpen]);
  
  // Calculate rotation based on scroll position
  useEffect(() => {
    const calculateRotationFromScroll = () => {
      if (!isMenuPopupOpen || isMenuRotating || isManualScrolling) return;
      
      const scrollPosition = window.scrollY;
      const viewportHeight = window.innerHeight;
      const scrollThreshold = viewportHeight * 0.3; // Section is active when its top is 30% from top
      
      // Get all section positions
      const sectionPositions = menuItems.map((item: { path: string; label: string }) => {
        const element = document.querySelector(item.path);
        if (!element) return { path: item.path, top: 0, bottom: 0, index: -1 };
        const rect = element.getBoundingClientRect();
        const top = rect.top + window.pageYOffset;
        const bottom = top + rect.height;
        return { path: item.path, top, bottom, index: menuItems.findIndex((m: { path: string }) => m.path === item.path) };
      }).filter((s: { index: number }) => s.index !== -1);
      
      if (sectionPositions.length === 0) return;
      
      // Find active section based on scroll position
      let activeIndex = 0;
      const currentScroll = scrollPosition + scrollThreshold;
      
      for (let i = 0; i < sectionPositions.length; i++) {
        if (currentScroll >= sectionPositions[i].top && 
            (i === sectionPositions.length - 1 || currentScroll < sectionPositions[i + 1].top)) {
          activeIndex = i;
          break;
        }
      }
      
      // If scrolled past last section, use last section
      if (currentScroll >= sectionPositions[sectionPositions.length - 1].top) {
        activeIndex = sectionPositions.length - 1;
      }
      
      setActiveSectionIndex(activeIndex);
      
      // Calculate rotation to put active section at 12 o'clock (0°)
      const anglePerItem = 360 / menuItems.length;
      const baseAngle = (activeIndex * anglePerItem) % 360;
      // We want baseAngle + rotation = 0, so rotation = -baseAngle
      const targetRotation = -baseAngle;
      
      // Smooth transition based on scroll position within section
      if (activeIndex < sectionPositions.length - 1) {
        const currentSection = sectionPositions[activeIndex];
        const nextSection = sectionPositions[activeIndex + 1];
        const sectionStart = currentSection.top;
        const sectionEnd = nextSection.top;
        const sectionLength = sectionEnd - sectionStart;
        
        if (sectionLength > 0) {
          const scrollProgress = Math.max(0, Math.min(1, (currentScroll - sectionStart) / sectionLength));
          const nextBaseAngle = ((activeIndex + 1) * anglePerItem) % 360;
          const nextTargetRotation = -nextBaseAngle;
          
          // Interpolate rotation
          const interpolatedRotation = targetRotation + (nextTargetRotation - targetRotation) * scrollProgress;
          setMenuRotation(interpolatedRotation);
        } else {
          setMenuRotation(targetRotation);
        }
      } else {
        setMenuRotation(targetRotation);
      }
    };
    
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const wasScrolled = isScrolled;
      const viewportHeight = window.innerHeight;
      
      // Update scrollY für Header-Position - nur wenn sich signifikant geändert hat (Throttling)
      if (Math.abs(scrollPosition - headerScrollYRef.current) > 5) {
        headerScrollYRef.current = scrollPosition;
        setHeaderScrollY(scrollPosition);
      }
      
      // Header fadet erst weg, wenn man die volle Viewport-Höhe (100vh) durchgescrollt hat
      // Das entspricht dem HeaderVideoContainer, der 100vh hoch ist
      const nowScrolled = scrollPosition >= viewportHeight;
      if (nowScrolled !== wasScrolled) {
        setIsScrolled(nowScrolled);
      }
      setShowScrollTop(scrollPosition > 300);
      
      // Calculate scroll progress (0 to 1)
      const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = documentHeight > 0 ? Math.min(scrollPosition / documentHeight, 1) : 0;
      setScrollProgress(progress);
      
      // Schließe Menü wenn man zurück in den Header scrollt
      if (wasScrolled && !nowScrolled && isMenuPopupOpen) {
        setMenuRotation(0); // Logo-Rotation zurücksetzen beim Schließen
        setIsMenuPopupOpen(false);
        setIsMenuRotating(false);
      }
      
      if (isMenuPopupOpen && !isMenuRotating) {
        calculateRotationFromScroll();
      }
      
      lastScrollYRef.current = scrollPosition;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial calculation
    if (isMenuPopupOpen) {
      calculateRotationFromScroll();
    }
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMenuPopupOpen, isMenuRotating, isManualScrolling, isScrolled, menuItems]);

  // Global ESC-Handler: Entfernt Fokus wenn keine Popups offen sind
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Nur ESC verarbeiten
      if (e.key !== 'Escape') return;

      // Prüfe ob ein Popup/Modal offen ist
      const hasOpenPopup = 
        isSearchOpen ||
        isAccessibilityOpen ||
        isMenuPopupOpen ||
        document.querySelector('[data-text-to-speech-player]') ||
        // Prüfe auf Cookie Manager Modal/Banner
        Array.from(document.querySelectorAll('[class*="Cookie"], [id*="cookie"]')).some(el => {
          const style = window.getComputedStyle(el);
          return style.display !== 'none' && 
                 style.visibility !== 'hidden' && 
                 parseFloat(style.opacity) > 0;
        }) ||
        // Prüfe auf Overlays mit hohem z-index
        Array.from(document.querySelectorAll('[style*="z-index"]')).some(el => {
          const style = window.getComputedStyle(el);
          const zIndex = parseInt(style.zIndex) || 0;
          const isVisible = style.display !== 'none' && 
                           style.visibility !== 'hidden' && 
                           parseFloat(style.opacity) > 0;
          return zIndex >= 10000 && isVisible;
        });
      
      // Wenn kein Popup offen ist, entferne Fokus
      if (!hasOpenPopup) {
        if (document.activeElement instanceof HTMLElement) {
          // Prüfe ob aktives Element fokussierbar ist (nicht body/html)
          const tagName = document.activeElement.tagName.toLowerCase();
          if (tagName !== 'body' && tagName !== 'html') {
            document.activeElement.blur();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSearchOpen, isAccessibilityOpen, isMenuPopupOpen]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleMenuPopupClick = (itemPath: string) => {
    if (isMenuRotating) return;
    
    // Find clicked item index
    const clickedIdx = menuItems.findIndex(item => item.path === itemPath);
    if (clickedIdx === -1) return;
    
    // Wenn wir nicht auf der Home-Seite sind, navigiere zuerst zur Home-Seite
    if (location.pathname !== '/') {
      // Setze pendingScrollAnchor für nach der Navigation
      setPendingScrollAnchor(itemPath);
      // Navigiere zur Home-Seite
      navigate('/');
      // Schließe das Menü
      setMenuRotation(0); // Menü-Rotation zurücksetzen beim Schließen
      setIsMenuPopupOpen(false);
      setIsMenuRotating(false);
      setPopupOpacity(1);
      return;
    }
    
    // Wir sind bereits auf der Home-Seite, fahre mit normalem Scroll fort
    // Get current scroll position
    const currentScrollPosition = window.scrollY;
    
    // Get target scroll position - calculate immediately before any DOM changes
    const element = document.querySelector(itemPath);
    let targetScrollPosition = 0;
    if (element) {
      const headerHeight = 0;
      const extraOffset = -30;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      targetScrollPosition = Math.max(0, elementPosition - headerHeight - extraOffset);
    }
    
    // Determine scroll direction
    const scrollingDown = targetScrollPosition > currentScrollPosition;
    
    const anglePerItem = 360 / menuItems.length;
    
    // Calculate the base angle for this item (without rotation)
    const baseAngle = (clickedIdx * anglePerItem) % 360;
    
    // Calculate current angle of clicked item (with current rotation)
    const currentAngle = (baseAngle + menuRotation) % 360;
    const normalizedAngle = currentAngle < 0 ? currentAngle + 360 : currentAngle;
    
    // Calculate rotation needed to move clicked item to top (0° = 12 o'clock)
    // Choose rotation direction based on scroll direction
    let rotationNeeded;
    if (scrollingDown) {
      // Scrolling down → rotate clockwise (positive)
      // Find shortest clockwise path
      rotationNeeded = normalizedAngle === 0 ? 0 : 360 - normalizedAngle;
    } else {
      // Scrolling up → rotate counter-clockwise (negative)
      // Find shortest counter-clockwise path
      rotationNeeded = -normalizedAngle;
    }
    
    const targetRotation = menuRotation + rotationNeeded;
    
    setIsMenuRotating(true);
    setIsManualScrolling(true);
    setMenuRotation(targetRotation);
    
    // Start scrolling immediately (simultaneously with rotation and fade)
    // Use requestAnimationFrame to ensure it happens
    requestAnimationFrame(() => {
      window.scrollTo({
        top: targetScrollPosition,
        behavior: 'smooth'
      });
    });
    
    // Start fade out animation
    const fadeDuration = ANIMATION_DURATION * 1000;
    const fadeSteps = 20;
    const fadeInterval = fadeDuration / fadeSteps;
    let currentStep = 0;
    
    const fadeIntervalId = setInterval(() => {
      currentStep++;
      const opacity = 1 - (currentStep / fadeSteps);
      setPopupOpacity(opacity);
      
      if (currentStep >= fadeSteps) {
        clearInterval(fadeIntervalId);
        setMenuRotation(0); // Menü-Rotation zurücksetzen beim Schließen
        setIsMenuPopupOpen(false);
        setIsMenuRotating(false);
        setIsManualScrolling(false);
        setPopupOpacity(1); // Reset for next time
      }
    }, fadeInterval);
  };

  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=Montserrat:wght@300;400;500;600;700;800&family=Allura&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  // Keyboard shortcut for search (CMD+K / CTRL+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Handle scrolling to anchor after navigation to home page
  useEffect(() => {
    if (location.pathname === '/' && pendingScrollAnchor) {
      // Wait a bit for page to render
      setTimeout(() => {
        const element = document.querySelector(pendingScrollAnchor);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        setPendingScrollAnchor(null);
      }, 200);
    }
  }, [location.pathname, pendingScrollAnchor]);

  // Scroll-to-Focus: Automatisch zum fokussierten Element scrollen
  useEffect(() => {
    const handleFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target) {
        // Kleine Verzögerung, damit das Element gerendert ist
        setTimeout(() => {
          const rect = target.getBoundingClientRect();
          const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
          const headerElement = document.querySelector('header');
          const isInHeader = headerElement ? headerElement.contains(target) : false;
          const isSkipLink = target.classList.contains('skip-to-content');
          const sideButtonsContainer = document.getElementById('side-buttons');
          const isInSideButtons = sideButtonsContainer ? sideButtonsContainer.contains(target) : false;
          const introElement = document.getElementById('intro');
          const isInIntro = introElement ? introElement.contains(target) : false;

          // Wenn das fokussierte Element das Intro-Element ist, nicht scrollen, da der onClick-Handler das bereits erledigt
          if (isInIntro) {
            return;
          }
          
          // Wenn Element im Header ist, Skip-Link oder Side-Button, IMMER ganz nach oben scrollen
          if (isInHeader || isSkipLink || isInSideButtons) {
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          
          // Für alle anderen Elemente: Normale Scroll-Logik
          const headerHeight = 120; // Header-Höhe
          const padding = 20; // Zusätzlicher Abstand
          const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const elementTop = rect.top + currentScrollTop;
          
          const isAboveViewport = rect.top < headerHeight + padding;
          const isBelowViewport = rect.bottom > viewportHeight - padding;
          const isOptimallyVisible = (
            rect.top >= headerHeight + padding &&
            rect.bottom <= viewportHeight - padding &&
            rect.left >= 0 &&
            rect.right <= (window.innerWidth || document.documentElement.clientWidth)
          );
          
          if (!isOptimallyVisible) {
            let scrollPosition: number;
            if (isAboveViewport) {
              scrollPosition = Math.max(0, elementTop - headerHeight - padding);
            } else if (isBelowViewport) {
              scrollPosition = elementTop - headerHeight - padding;
            } else {
              scrollPosition = elementTop - headerHeight - padding;
            }
            
            if (Math.abs(currentScrollTop - scrollPosition) > 10) {
              window.scrollTo({
                top: scrollPosition,
                behavior: 'smooth'
              });
            }
          }
        }, 100);
      }
    };

    document.addEventListener('focusin', handleFocus);
    return () => document.removeEventListener('focusin', handleFocus);
  }, []);



  return (
    <>
      <SEOHead />
      {/* Skip-Links MÜSSEN am Anfang stehen für korrekte Tab-Reihenfolge */}
      {/* Skip to Content Link für Tastaturnavigation - Erste Tab-Reihenfolge */}
      <a 
        href="#intro" 
        className="skip-to-content"
        onClick={(e) => {
          e.preventDefault();
          // Warte kurz, damit die Seite gerendert ist
          setTimeout(() => {
            const introSection = document.getElementById('intro');
            if (introSection) {
              // Focus auf Intro setzen
              introSection.focus();
              
              // Verwende die gleiche Logik wie die Menü-Links
              const headerHeight = 0;
              const extraOffset = -200; // Mehr Offset für bessere Sichtbarkeit
              const elementPosition = introSection.getBoundingClientRect().top + window.pageYOffset;
              const scrollPosition = Math.max(0, elementPosition - headerHeight - extraOffset);
              
              // Scroll zum Intro-Bereich
              window.scrollTo({
                top: scrollPosition,
                behavior: 'smooth'
              });
            }
          }, 100);
        }}
      >
          Zum Hauptinhalt springen
        </a>
        
      {/* Skip to Side-Buttons Link - Zweite Tab-Reihenfolge */}
      <a 
        href="#side-buttons" 
        className="skip-to-content"
        onClick={(e) => {
          e.preventDefault();
          const sideButtonsContainer = document.getElementById('side-buttons');
          if (sideButtonsContainer) {
            // Focus auf ersten Button setzen (LanguageToggle)
            const firstButton = sideButtonsContainer.querySelector('button') as HTMLElement;
            if (firstButton) {
              // Focus direkt setzen
              firstButton.focus();
              const headerHeight = 100;
              const elementPosition = firstButton.getBoundingClientRect().top + window.pageYOffset;
              const offsetPosition = elementPosition - headerHeight;
              window.scrollTo({
                top: Math.max(0, offsetPosition),
                behavior: 'smooth'
              });
            }
          }
        }}
      >
        Zu den Sidebuttons springen
      </a>
      
      <S.GlobalStyle>
        {/* Modern einfarbiger Hintergrund */}
        <S.Background colors={colors} />
        
        
        <S.LayoutWrapper 
        style={{ 
          background: 'transparent'
        }}>
        <div style={{ minHeight: '100vh', background: 'transparent', display: 'flex', flexDirection: 'column' }}>
          <S.Header isScrolled={isScrolled} colors={colors} menuItemCount={menuItems.length} scrollY={headerScrollY}>
            <S.HeaderCenter>
              {menuItems.length > 0 && (
              <S.Nav>
                {menuItems.map((item) => (
                  <S.NavLink
                    key={item.path}
                    href={item.path}
                    active={false}
                    colors={colors}
                    onClick={(e) => {
                      e.preventDefault();
                      // Remove focus after mouse click
                      if (e.currentTarget instanceof HTMLElement) {
                        e.currentTarget.blur();
                      }
                      // If not on home page, navigate to home first and store anchor
                      if (location.pathname !== '/') {
                        setPendingScrollAnchor(item.path);
                        navigate('/');
                      } else {
                        // Already on home page, scroll with offset for header
                        setTimeout(() => {
                          const element = document.querySelector(item.path);
                          if (element) {
                            const headerHeight = 0;
                            const extraOffset = -30; // Zusätzlicher Abstand nach unten
                            const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                            const offsetPosition = elementPosition - headerHeight - extraOffset;
                            window.scrollTo({
                              top: Math.max(0, offsetPosition),
                              behavior: 'smooth'
                            });
                          }
                        }, 150);
                      }
                    }}
                    onMouseDown={(e) => {
                      // Prevent focus on mouse down
                      if (e.button === 0) { // Left mouse button
                        e.preventDefault();
                      }
                    }}
                  >
                    {item.label}
                  </S.NavLink>
                ))}
                  {kamanaSubmenu.length > 0 && (
                <S.DropdownContainer>
                  <S.DropdownButton
                    colors={colors}
                    isOpen={location.pathname.startsWith('/kamana')}
                    onClick={() => {
                      if (!location.pathname.startsWith('/kamana')) {
                        navigate('/kamana/about');
                      }
                    }}
                  >
                    Kamana
                  </S.DropdownButton>
                  <AnimatePresence>
                    {location.pathname.startsWith('/kamana') && (
                      <S.DropdownMenu
                        colors={colors}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                      >
                        {kamanaSubmenu.map((item) => (
                          <S.DropdownItem
                            key={item.path}
                            colors={colors}
                            href={item.path}
                            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                              e.preventDefault();
                              navigate(item.path);
                            }}
                          >
                            {item.label}
                          </S.DropdownItem>
                        ))}
                      </S.DropdownMenu>
                    )}
                  </AnimatePresence>
                </S.DropdownContainer>
                  )}
              </S.Nav>
              )}
            </S.HeaderCenter>
            {menuItems.length > 0 && (
            <S.MobileMenuButton colors={colors} onClick={(e) => {
              lastScrollYRef.current = window.scrollY;
              setPopupOpacity(1);
              setIsMenuPopupOpen(true);
              // Remove focus after mouse click
              if (e.currentTarget instanceof HTMLElement) {
                e.currentTarget.blur();
              }
            }}
            onMouseDown={(e) => {
              // Prevent focus on mouse down
              if (e.button === 0) { // Left mouse button
                e.preventDefault();
              }
            }}>
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            </S.MobileMenuButton>
            )}
          </S.Header>
          
          {/* Side-Buttons Container NACH dem Header - Tab-Reihenfolge */}
          <div id="side-buttons" style={{ position: 'relative' }}>
            {/* 1. Language Toggle */}
            <S.LanguageToggle
              colors={colors}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={(e) => {
                handleLanguageToggle();
                // Remove focus after mouse click
                if (e.currentTarget instanceof HTMLElement) {
                  e.currentTarget.blur();
                }
              }}
              onMouseDown={(e) => {
                // Prevent focus on mouse down
                if (e.button === 0) { // Left mouse button
                  e.preventDefault();
                }
              }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              aria-label={language === 'de' ? 'Switch to English' : 'Zu Deutsch wechseln'}
              title={language === 'de' ? 'Switch to English' : 'Zu Deutsch wechseln'}
            >
              {language === 'de' ? 'EN' : 'DE'}
            </S.LanguageToggle>
            
            {/* 2. Contact FABs - Telefon und Email */}
            <S.ContactFabGroup colors={colors}>
              <S.ContactFab
                colors={colors}
                href="tel:+436503903381"
                aria-label="Telefon anrufen"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.08 4.18 2 2 0 0 1 4.06 2h3a2 2 0 0 1 2 1.72c.12.9.33 1.77.63 2.6a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.48-1.15a2 2 0 0 1 2.11-.45c.83.3 1.7.51 2.6.63A2 2 0 0 1 22 16.92z"/>
                </svg>
              </S.ContactFab>
              <S.ContactFab
                colors={colors}
                href="mailto:hello@kamana.at"
                aria-label="E-Mail senden"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"/>
                  <path d="m22 6-10 7L2 6"/>
                </svg>
              </S.ContactFab>
            </S.ContactFabGroup>
            
            {/* 3. Search Button */}
            <S.SearchButton
              colors={colors}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={(e) => {
                setIsSearchOpen(true);
                // Remove focus after mouse click
                if (e.currentTarget instanceof HTMLElement) {
                  e.currentTarget.blur();
                }
              }}
              onMouseDown={(e) => {
                // Prevent focus on mouse down
                if (e.button === 0) { // Left mouse button
                  e.preventDefault();
                }
              }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              aria-label="Suche öffnen"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
            </S.SearchButton>
            
            {/* 4. Accessibility Button */}
            <S.AccessibilityButton
              colors={colors}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={(e) => {
                setIsAccessibilityOpen(true);
                // Remove focus after mouse click
                if (e.currentTarget instanceof HTMLElement) {
                  e.currentTarget.blur();
                }
              }}
              onMouseDown={(e) => {
                // Prevent focus on mouse down
                if (e.button === 0) { // Left mouse button
                  e.preventDefault();
                }
              }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              aria-label="Barrierefreiheit"
            >
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="7" r="1.5" fill="currentColor"/>
                <line x1="12" y1="8.5" x2="12" y2="14"/>
                <line x1="7" y1="11" x2="17" y2="11"/>
                <line x1="10" y1="18" x2="12" y2="14"/>
                <line x1="14" y1="18" x2="12" y2="14"/>
              </svg>
            </S.AccessibilityButton>
          </div>
          
          {/* Old mobile menu removed - using rotating menu instead */}
          <S.Main id="main-content">
            {children}
          </S.Main>
          <Footer />
          
          <AnimatePresence>
            {showScrollTop && (
              <S.ScrollToTopButton
                colors={colors}
                scrollProgress={scrollProgress}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                onClick={scrollToTop}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                title="Nach oben scrollen"
              >
                <svg viewBox="0 0 45 45" width="45" height="45" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                  {/* Background circle */}
                  <circle
                    cx="22.5"
                    cy="22.5"
                    r="17"
                    fill="none"
                    stroke={colors.primary}
                    strokeWidth="1.5"
                    opacity="0.2"
                  />
                  {/* Progress circle */}
                  <circle
                    cx="22.5"
                    cy="22.5"
                    r="17"
                    fill="none"
                    stroke={colors.primary}
                    strokeWidth="1.5"
                    strokeDasharray={`${2 * Math.PI * 17}`}
                    strokeDashoffset={`${2 * Math.PI * 17 * (1 - scrollProgress)}`}
                    transform="rotate(-90 22.5 22.5)"
                    strokeLinecap="round"
                  />
                </svg>
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'relative', zIndex: 1 }}>
                  <line x1="12" y1="19" x2="12" y2="5"></line>
                  <polyline points="5 12 12 5 19 12"></polyline>
                </svg>
              </S.ScrollToTopButton>
            )}
          </AnimatePresence>
          
          {/* Rotating menu button - only visible when scrolled on desktop */}
          {isScrolled && (
            <S.MenuPopupButton
              colors={colors}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              onClick={(e) => {
                lastScrollYRef.current = window.scrollY;
                setPopupOpacity(1);
                setIsMenuPopupOpen(true);
                // Remove focus after mouse click
                if (e.currentTarget instanceof HTMLElement) {
                  e.currentTarget.blur();
                }
              }}
              onMouseDown={(e) => {
                // Prevent focus on mouse down
                if (e.button === 0) { // Left mouse button
                  e.preventDefault();
                }
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Menü öffnen"
            >
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            </S.MenuPopupButton>
          )}
          
          <AnimatePresence>
            {isMenuPopupOpen && (
              <>
                <S.MenuPopupOverlay
                  initial={{ opacity: 0 }}
                  animate={{ opacity: popupOpacity * 0.7 }}
                  exit={{ opacity: 0 }}
                  onClick={() => {
                    setMenuRotation(0); // Menü-Rotation zurücksetzen beim Schließen
                    setIsMenuPopupOpen(false);
                  }}
                />
                <S.MenuPopupContainer
                  colors={colors}
                  initial={{ opacity: 0, scale: 0.8, x: '-50%', y: '-50%' }}
                  animate={{ opacity: popupOpacity, scale: 1, x: '-50%', y: '-50%' }}
                  exit={{ opacity: 0, scale: 0.8, x: '-50%', y: '-50%' }}
                  transition={{ duration: 0.3 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <S.MenuWrapper
                    colors={colors}
                    animate={{ rotate: menuRotation }}
                    transition={{ 
                      duration: isMenuRotating ? ANIMATION_DURATION : 0.3, 
                      ease: isMenuRotating ? [0.77, 0, 0.18, 1] : 'easeOut' 
                    }}
                  >
                    {menuItems.map((item, idx) => {
                      const angle = (2 * Math.PI * idx) / menuItems.length - Math.PI / 2; // Start at top (12 o'clock)
                      
                      // Responsive values based on screen size
                      const isMobile = window.innerWidth <= 480;
                      const isTablet = window.innerWidth > 480 && window.innerWidth <= 768;
                      
                      const currentRadius = isMobile ? RADIUS_MOBILE : (isTablet ? RADIUS_TABLET : RADIUS);
                      const currentCenter = isMobile ? CENTER_MOBILE : (isTablet ? CENTER_TABLET : CENTER);
                      
                      // Calculate positions relative to center (50% of container)
                      const x = currentCenter + currentRadius * Math.cos(angle);
                      const y = currentCenter + currentRadius * Math.sin(angle);
                      const counterRotation = -menuRotation;
                      const isActive = idx === activeSectionIndex;
                      
                      return (
                        <motion.div
                          key={item.path}
                          style={{ 
                            position: 'absolute', 
                            left: x, 
                            top: y, 
                            transform: 'translate(-50%, -50%)',
                            margin: 0,
                            padding: 0
                          }}
                          animate={{ rotate: counterRotation }}
                          transition={{ 
                            duration: isMenuRotating ? ANIMATION_DURATION : 0.3, 
                            ease: isMenuRotating ? [0.77, 0, 0.18, 1] : 'easeOut' 
                          }}
                        >
                          <S.MenuButton
                            x={0}
                            y={0}
                            active={isActive}
                            colors={colors}
                            onClick={() => handleMenuPopupClick(item.path)}
                            disabled={isMenuRotating}
                            counterRotation={0}
                          >
                            {item.label}
                          </S.MenuButton>
                        </motion.div>
                      );
                    })}
                  </S.MenuWrapper>
                  <S.LogoCircle colors={colors}>
                    <motion.img
                      src="/yoga/kamana-logo.png"
                      alt="Kamana Logo"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                      animate={{ 
                        opacity: popupOpacity
                      }}
                      transition={{ 
                        duration: 0.3, 
                        ease: 'easeOut' 
                      }}
                    />
                  </S.LogoCircle>
                </S.MenuPopupContainer>
              </>
            )}
          </AnimatePresence>
          
          {isScrolled && (
            <S.MobileButtonBar>
              <S.MobileLanguageToggle
              colors={colors}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={(e) => {
                handleLanguageToggle();
                // Remove focus after mouse click
                if (e.currentTarget instanceof HTMLElement) {
                  e.currentTarget.blur();
                }
              }}
              onMouseDown={(e) => {
                // Prevent focus on mouse down
                if (e.button === 0) { // Left mouse button
                  e.preventDefault();
                }
              }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              title={language === 'de' ? 'Switch to English' : 'Zu Deutsch wechseln'}
            >
              {language === 'de' ? 'EN' : 'DE'}
            </S.MobileLanguageToggle>
            <S.ContactFab
              colors={colors}
              href="tel:+436503903381"
              aria-label="Telefon anrufen"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.08 4.18 2 2 0 0 1 4.06 2h3a2 2 0 0 1 2 1.72c.12.9.33 1.77.63 2.6a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.48-1.15a2 2 0 0 1 2.11-.45c.83.3 1.7.51 2.6.63A2 2 0 0 1 22 16.92z"/>
              </svg>
            </S.ContactFab>
            <S.ContactFab
              colors={colors}
              href="mailto:hello@kamana.at"
              aria-label="E-Mail senden"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"/>
                <path d="m22 6-10 7L2 6"/>
              </svg>
            </S.ContactFab>
            <S.MobileSearchButton
              colors={colors}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={(e) => {
                setIsSearchOpen(true);
                // Remove focus after mouse click
                if (e.currentTarget instanceof HTMLElement) {
                  e.currentTarget.blur();
                }
              }}
              onMouseDown={(e) => {
                // Prevent focus on mouse down
                if (e.button === 0) { // Left mouse button
                  e.preventDefault();
                }
              }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
            </S.MobileSearchButton>
            <S.MobileAccessibilityButton
              colors={colors}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={(e) => {
                setIsAccessibilityOpen(true);
                // Remove focus after mouse click
                if (e.currentTarget instanceof HTMLElement) {
                  e.currentTarget.blur();
                }
              }}
              onMouseDown={(e) => {
                // Prevent focus on mouse down
                if (e.button === 0) { // Left mouse button
                  e.preventDefault();
                }
              }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              aria-label="Barrierefreiheit"
            >
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="7" r="1.5" fill="currentColor"/>
                <line x1="12" y1="8.5" x2="12" y2="14"/>
                <line x1="7" y1="11" x2="17" y2="11"/>
                <line x1="10" y1="18" x2="12" y2="14"/>
                <line x1="14" y1="18" x2="12" y2="14"/>
              </svg>
            </S.MobileAccessibilityButton>
            <S.MobileMenuButtonBar
              colors={colors}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              onClick={(e) => {
                lastScrollYRef.current = window.scrollY;
                setPopupOpacity(1);
                setIsMenuPopupOpen(true);
                // Remove focus after mouse click
                if (e.currentTarget instanceof HTMLElement) {
                  e.currentTarget.blur();
                }
              }}
              onMouseDown={(e) => {
                // Prevent focus on mouse down
                if (e.button === 0) { // Left mouse button
                  e.preventDefault();
                }
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Menü öffnen"
              title="Menü öffnen"
            >
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            </S.MobileMenuButtonBar>
            </S.MobileButtonBar>
          )}
          <SearchPalette 
            isOpen={isSearchOpen} 
            onClose={() => setIsSearchOpen(false)} 
          />
          <AccessibilityPanel 
            isOpen={isAccessibilityOpen} 
            onClose={() => setIsAccessibilityOpen(false)} 
          />
          <EventPopup />
        </div>
        </S.LayoutWrapper>
      </S.GlobalStyle>
    </>
  );
};

export default Layout; 